import { useCallback, useEffect, useState, type RefObject } from "react";
import { env } from "@shared/config.ts";
import { useIframeStore } from "../model";

interface IUseLegacyIframe {
  parentRef: RefObject<HTMLElement | null>;
  route: string;
}

const messageRegEx = /^(\w+):(\w+)(?::(.+))?$/;

export function useLegacyIframe({ parentRef, route }: IUseLegacyIframe) {
  const {
    iframeRef,
    setSize,
    setIsVisible,
    isIframeInitialized,
    setIsIframeInitialized,
    isIframeBlocked,
    setIsIframeBlocked,
    messageQueue,
    popMessageFromQueue,
    pushMessageToQueue,
  } = useIframeStore();

  const onMessage = useCallback(
    (e: MessageEvent<any>) => {
      if (
        !iframeRef ||
        typeof e.data !== "string" ||
        !messageRegEx.test(e.data)
      )
        return;

      const [, namespace, action] = e.data.match(messageRegEx) ?? [];

      if (namespace !== "builddocs" || e.origin !== env.OLD_FRONTEND_URL)
        return;

      switch (action) {
        case "response": {
          setIsIframeBlocked(false);
          popMessageFromQueue();
          break;
        }
        default: {
          break;
        }
      }
    },
    [
      iframeRef,
      isIframeInitialized,
      setIsIframeBlocked,
      setIsIframeInitialized,
    ],
  );

  const sendMessage = useCallback(() => {
    if (isIframeInitialized === 2 && !isIframeBlocked) {
      const message = messageQueue.at(-1);
      if (!message) return;
      setIsIframeBlocked(true);
      iframeRef?.current?.contentWindow?.postMessage(message, "*");
    }
  }, [
    iframeRef,
    isIframeBlocked,
    isIframeInitialized,
    messageQueue,
    setIsIframeBlocked,
  ]);

  const [observer] = useState(
    () =>
      new ResizeObserver((entries: ResizeObserverEntry[]) => {
        const [entry] = entries ?? [];
        if (entry) {
          setSize(entry.borderBoxSize.at(0)!);
        }
      }),
  );

  useEffect(() => {
    const element = parentRef?.current;
    if (element) {
      observer.observe(element);
      setIsVisible(true);
    }
    return () => {
      if (element) {
        observer.unobserve(element);
        setIsVisible(false);
      }
    };
  }, [observer, parentRef, setSize, setIsVisible]);

  /* iframe messaging */
  /* send */
  useEffect(() => {
    pushMessageToQueue(`builddocs:navigate:${route}`);
  }, [pushMessageToQueue, route]);

  useEffect(() => {
    if (iframeRef && !isIframeBlocked && messageQueue.length) {
      setIsIframeBlocked(false);
    }
  }, [iframeRef, isIframeBlocked, messageQueue, setIsIframeBlocked]);

  /* receive */
  useEffect(() => {
    window.addEventListener("message", onMessage);

    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef, onMessage]);

  useEffect(() => {
    if (isIframeInitialized && !isIframeBlocked && messageQueue.length) {
      sendMessage();
    }
  });

  return {
    isLoading: isIframeInitialized !== 2,
    isPending: isIframeBlocked || !!messageQueue.length,
  };
}
